import BreadCrumb from "Components/Common/BreadCrumb";
import { Card, Container } from "reactstrap";
import calendar from "../../assets/images/calendarOutline.png";
import ico from "../../assets/images/Info-icon.png";
import download from "../../assets/images/Frame 1272628235.png";
import chart from "../../assets/images/Chart.png";
import "./KeyMetrics.scss";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ProductPerformance from "./productperfomance";
import { getKeyMetricsThunk, getQSDBThunk } from "live-slices/thunk";
import { createSelector } from "reselect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Constant } from "utils/enums";
import ErrorModal from "Components/Common/ErrorModal";


const allKeys: MetricKey[] = [
  'revenue',
  'visitors',
  'conversionRate',
  'revenuePerBuyer',
  'pageViews',
];

type MetricKey = 'revenue' | 'visitors' | 'conversionRate' | 'revenuePerBuyer' | 'pageViews';

const COLORS: Record<MetricKey, string> = {
  revenue: '#3366FF',
  visitors: '#00CC99',
  conversionRate: '#ffc658',
  revenuePerBuyer: '#ff7300',
  pageViews: '#0088FE',
};

const options = [
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "last7" },
  { label: "Last 30 Days", value: "last30" },
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
];

function getDateRange(type: string): [Date, Date] {
  const today = new Date();
  let start = new Date();
  let end = new Date();

  if (type === "yesterday") {
    start.setDate(today.getDate() - 1);
    end.setDate(today.getDate() - 1);
  } else if (type === "last7") {
    start.setDate(today.getDate() - 7);
  } else if (type === "last30") {
    start.setDate(today.getDate() - 30);
  }

  return [start, end];
}

function getRangeLabel(start: Date, end: Date) {
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
}



const KeyMetrics = () => {
  // const chartData = useSelector((state: any) => state?.chart?.data ?? []);
  // const allKeys = ['revenue', 'visitor', 'conversionRate', 'revenuePerBuyer', 'pageView'];
  const [selectedLines, setSelectedLines] = useState<MetricKey[]>(allKeys);
  const [activeTab, setActiveTab] = useState('dashboard');
  const dispatch = useDispatch<any>();
  const [selectedKeyMetric, setSelectedKeyMetrics] = useState();
  const [selectedKeyMetricCounts, setSelectedKeyMetricsCounts] = useState<any>({});
  const [metricStates, setMetricStates] = useState({
    isRevenue: true,
    isVisitors: true,
    isConversionRate: false,
    isRevenuePerBuyer: false,
    isPageViews: false,
  });

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMode, setCalendarMode] = useState<"day" | "week" | "month">("day");
  const [selectedLabel, setSelectedLabel] = useState("Yesterday");
  const [selectedRange, setSelectedRange] = useState("yesterday");
  const selectedCount = Object.values(metricStates).filter(Boolean).length;
  const userStr = localStorage.getItem(Constant.seller);
  const user = userStr ? JSON.parse(userStr) : null;
  const sellerId = user?.sellerId;
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [showModal, setShowModal] = useState({
          isVisible: false,
          color: false,
        });


 const formatDate = (date: Date) => {
  return encodeURIComponent(date.toISOString());
};


const metricMap: Record<keyof typeof metricStates, string> = {
  isRevenue: "revenue",
  isVisitors: "visitors",
  isConversionRate: "conversionrate",
  isRevenuePerBuyer: "revenue_per_buyer",
  isPageViews: "page_views",
};


const getSelectedMetricTypes = (): string[] => {
  return Object.entries(metricStates)
    .filter(([_, isChecked]) => isChecked)
    .map(([key]) => metricMap[key as keyof typeof metricStates]);
};



const selectedTypes = getSelectedMetricTypes();


 const { dayLabel, yearLabel } = useMemo(() => {
    let dayLabel = "";
    let yearLabel = "";

    if (selectedRange === "yesterday" || selectedRange === "day") {
      dayLabel = "vs Previous Day";
      yearLabel = "Week over Week";
    } else if (selectedRange === "last7") {
      dayLabel = "vs Previous 7 Days";
    } else if (selectedRange === "last30") {
      dayLabel = "vs Previous 30 Days";
    } else if (selectedRange === "week") {
      dayLabel = "vs Previous Week";
      yearLabel = "Year over Year";
    } else if (selectedRange === "month") {
      dayLabel = "vs Previous Month";
      yearLabel = "Year over Year";
    }

    return { dayLabel, yearLabel };
  }, [selectedRange]);



  const sellerPerformanceData = createSelector(
    (state: any) => state.SellerPerformance,
    (qsdb) => qsdb?.qsdb
  );
  const keymetricsData = useSelector(sellerPerformanceData) || {};

  const sellerPerformanceCounts = createSelector(
    (state: any) => state.SellerPerformance,
    (keymetricsCount) => keymetricsCount?.keymetricsCount
  );
  const keymetricsCounts = useSelector(sellerPerformanceCounts) || {};



  const handleToggle = (key: MetricKey) => {
    setSelectedLines((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

const normalizeKey = (key: string): string => {
  switch (key) {
    // case "visitors":
    //   return "visitor";
    case "conversionrate":
      return "conversionRate";
    case "revenue_per_buyer":
      return "revenuePerBuyer";
    case "page_views":
      return "pageViews";
    default:
      return key; 
  }
};

const keymetricsArray = Object.values(keymetricsData || {}) as any[];

const firstWithYAxis = keymetricsArray.find((m: any) => m?.data?.yAxis?.length > 0);
const yAxisLabels = (firstWithYAxis as any)?.data?.yAxis || [];

const chartDataa = yAxisLabels.map((label: string, index: number) => {
  const row: any = { time: label };

  keymetricsArray.forEach((metric: any) => {
    const rawKey = metric.type; 
    const normKey = normalizeKey(rawKey); 
    const point = metric?.data?.xAxis?.[index] || {};
    row[normKey] = point[normKey] ?? 0;
  });

  return row;
});

const handleReset = () => {
  setMetricStates({
    isRevenue: true,
    isVisitors: true,
    isConversionRate: false,
    isRevenuePerBuyer: false,
    isPageViews: false,
  });
};

useEffect(() => {
  if (!startDate && !endDate) {
    const [start, end] = getDateRange("yesterday");
    setStartDate(start);
    setEndDate(end);
  }
  console.log(chartDataa)
}, [chartDataa]);


  useEffect(() => {
  // const [start, end] = getDateRange("yesterday");
  // setStartDate(start);
  // setEndDate(end);
      if (!keymetricsData) return;
  const mapped: any = {};

  Object.values(keymetricsData).forEach((item: any) => {
    const type = item?.type;
    const data = item?.data || {};
    const cleaned = {
      current: data.current ?? 0,
      percentChange: data.percentChange ?? 0,
      direction: data.direction ?? "neutral",
    };

    switch (type) {
      case "revenue":
        mapped.isRevenue = cleaned;
        break;
      case "visitors":
        mapped.isVisitors = cleaned;
        break;
      case "conversionrate":
        mapped.isConversionRate = cleaned;
        break;
      case "revenue_per_buyer":
        mapped.isRevenuePerBuyer = cleaned;
        break;
      case "page_views":
        mapped.isPageViews = cleaned;
        break;
      default:
        break
    }
  });
 setSelectedKeyMetricsCounts(mapped);}, 
 [keymetricsData]);


useEffect(() => {
  dispatch(
    getQSDBThunk({
      seller_id: sellerId,
      start_date: formatDate(startDate || new Date()),
      end_date: formatDate(endDate || new Date()),
      types: getSelectedMetricTypes(), 
    })
  );
}, [metricStates, startDate, endDate]);

  // const chartDataa = keymetricsData.yAxis?.map((label: any, index: number) => {
  //   const item = keymetricsData.xAxis?.[index] || {};
  //   return {
  //     time: label,
  //     revenue: item.revenue || 0,
  //     visitor: item.visitor || 0,
  //     conversionRate: item.conversionRate || 0,
  //     revenuePerBuyer: item.revenuePerBuyer || 0,
  //     pageView: item.pageView || 0,
  //   };
  // });



  const handleCheckboxChange = (key: keyof typeof metricStates) => {
    setMetricStates((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

const handleDownloadCSV = () => {
  if (!chartDataa || chartDataa.length === 0) {
     setShowModal({ isVisible: true, color: false });
     setErrorMessage(`Data not available!`);
     setTimeout(() => {
     setShowModal({ isVisible: false, color: false });
          }, 1000);
    return;
  }

  const headers = Object.keys(chartDataa[0]);
  const rows = chartDataa.map((obj: { [x: string]: any; }) => headers.map((h) => obj[h]).join(','));
  const csvContent = [headers.join(','), ...rows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'metrics-data.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


const hasData = chartDataa.length > 0 && selectedLines.some(key => chartDataa.some((d: { [x: string]: number; }) => d[key] !== 0));


  return (
    <div className="page-content key-metrics keyMatricsRes">
          <ErrorModal
                          show={showModal.isVisible}
                          message={errorMessage}
                          color={showModal.color}
                          onCloseClick={() => setShowModal({ isVisible: false, color: false })}
                        />
      <Container fluid>
        <BreadCrumb
          title="Keymetrcis"
          pageTitle="Keymetrics"
          pageTitleRoute="/key-metrics"
        />

        <div className="tabBarMainDev mobile-show">
          <div
            className={`tabDv ${activeTab === 'dashboard' ? 'activeTab' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </div>
          <div
            className={`tabDv ${activeTab === 'products' ? 'activeTab' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products Performance
          </div>
        </div>
       

        {activeTab === 'dashboard' && (
         <>
           <Card className="campaign-info-card mobileClenderCrad">
 <div
      className="campaign-header"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <div style={{ position: "relative" }}>
        <img
          src={calendar}
          alt="calendar"
          style={{ cursor: "pointer", width: "24px", height: "24px" }}
          onClick={() => setShowDropdown((prev) => !prev)}
        />

        {showDropdown && (
          <div className="dropdown-menu"
       
           >
            <div style={{ minWidth: "150px" }}>
              {options.map((option) => (
                <div
                  key={option.value}
                  style={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    setSelectedLabel(option.label);
                    setSelectedRange(option.value);

                    if (
                      option.value === "yesterday" ||
                      option.value === "last7" ||
                      option.value === "last30"
                    ) {
                      const [start, end] = getDateRange(option.value);
                      setStartDate(start);
                      setEndDate(end);
                      setShowCalendar(false);
                      setShowDropdown(false);
                    } else {
                      setCalendarMode(option.value as "day" | "week" | "month");
                      setShowCalendar(true);
                    }
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>

            {showCalendar && (
              <div>
                {calendarMode === "day" && (
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setStartDate(date);
                      setEndDate(date);
                      setShowCalendar(false);
                      setShowDropdown(false);
                    }}
                    inline
                  />
                )}

                {calendarMode === "week" && (
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      const selected = new Date(date);
                      const day = selected.getDay();
                      const monday = new Date(selected);
                      monday.setDate(selected.getDate() - ((day + 6) % 7));
                      const sunday = new Date(monday);
                      sunday.setDate(monday.getDate() + 6);
                      setStartDate(monday);
                      setEndDate(sunday);
                      setShowCalendar(false);
                      setShowDropdown(false);
                    }}
                    inline
                    dayClassName={(date) => {
                      if (!startDate || !endDate) return "";
                      const time = date.getTime();
                      return time >= startDate.getTime() && time <= endDate.getTime()
                        ? "week-highlight"
                        : "";
                    }}
                  />
                )}

                {calendarMode === "month" && (
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                      setStartDate(firstDay);
                      setEndDate(lastDay);
                      setShowCalendar(false);
                      setShowDropdown(false);
                    }}
                    showMonthYearPicker
                    dateFormat="MM/yyyy"
                    inline
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <h6>
        {selectedLabel} {startDate && endDate ? getRangeLabel(startDate, endDate) : ""}
      </h6>
    </div>


          </Card>
          <Card className="metrics-section">
            <div className="metrics-header">
              <h4>Key Metrics</h4>
              <div className="metrics-header-right" onClick={handleDownloadCSV}>
                <h5>Download</h5>
                <img
                  src={download}
                  alt="download"
                  className="metrics-header-img"
                />
              </div>
            </div>

<div className="metrics-cards">
  {Object.entries(metricStates).map(([key, checked]) => {
    
    const labelMap: Record<string, string> = {
      isRevenue: "Revenue",
      isVisitors: "Visitors",
      isConversionRate: "Conversion Rate",
      isRevenuePerBuyer: "Revenue per Buyer",
      isPageViews: "Page Views",
    };

    const data = selectedKeyMetricCounts?.[key] || {};
    const isCurrency = key === "isRevenue" || key === "isRevenuePerBuyer";
    const isPercentage = key === "isConversionRate";

    const arrow = data.percentChange < 0 ? "↓" : "↑";
    const arrowClass = data.percentChange < 0 ? "down" : "up";

    return (
      <div className={`metric-card ${checked ? "activeDv" : ""}`} key={key}>
        <div className="card-head">
          <span>{labelMap[key]} <img src={ico} alt="icon" /></span>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => handleCheckboxChange(key as keyof typeof metricStates)}
          />
        </div>

        {/* Current Value */}
        <div className="card-amount">
          {isCurrency && "PKR "}
          <strong>
            {data.current ?? 0}
            {isPercentage && " %"}
          </strong>
        </div>

        {/* Percent Change */}
        <div className="card-comparison">
          {dayLabel && (
            <div className="comparison-row">
              <span>{dayLabel}</span>
              <span className="percent">
                {Math.abs(data.percentChange ?? 0)}%
                <span className={arrowClass}> {arrow}</span>
              </span>
            </div>
          )}
          {yearLabel && (
            <div className="comparison-row">
              <span>{yearLabel}</span>
              <span className="percent">
                {Math.abs(data.percentChange ?? 0)}%
                <span className={arrowClass}> {arrow}</span>
              </span>
            </div>
          )}
        </div>
      </div>
    );
  })}
</div>




            <div className="metrics-chart-container">
              <div className="chart-info-header">
                <div className="chart-info-left">
                  <div className="metric-toggles">
                    {allKeys
  .filter((key) => {
    const stateKey = `is${key.charAt(0).toUpperCase()}${key.slice(1)}` as keyof typeof metricStates;
    return metricStates[stateKey];
  })
  .map((key: MetricKey) => {
    const isSelected = selectedLines.includes(key);
    return (
      <span
        key={key}
        onClick={() => handleToggle(key)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          marginRight: 16,
          cursor: 'pointer',
          color: isSelected ? COLORS[key] : '#ccc',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            backgroundColor: isSelected ? COLORS[key] : '#ccc',
            marginRight: 6,
          }}
        />
        {key.charAt(0).toUpperCase() + key.slice(1)}
      </span>
    );
  })}

                  </div>
                </div>
                <div className="chart-info-right">
                  <span>Selected {selectedCount}/5</span>
                  <button
                    className="reset-btn"
                    data-testing-id="checkbox_reset_metric_card"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="metrics-chart">

        {chartDataa && chartDataa.length > 0 ? (
  <ResponsiveContainer width="100%" height={400}>
    <LineChart data={chartDataa}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="time"
        angle={-30}
        textAnchor="end"
        interval={0}
      />
      <YAxis />
      <Tooltip />
      <Legend />

      {selectedLines.map((key) => {
        const color = COLORS[key as MetricKey] || "#8884d8";
        return (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={color}
            strokeWidth={2}
            dot={{ r: 0 }}
            activeDot={{ r: 4 }}
          />
        );
      })}
    </LineChart>
  </ResponsiveContainer>
) : (
  <div>No chart data available for the selected metrics and date range.</div>
)}




              </div>
            </div>
          </Card>
         </>
        )}
        <div className="mobile-show">
          {activeTab === 'products' && (
            <ProductPerformance />
          )}
        </div>

      </Container>
    </div>
  );
};

export default KeyMetrics;
